import { create } from 'zustand';
import api from '@/lib/api';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type NotificationCategory = 'system' | 'billing' | 'network' | 'subscriber' | 'ticket';

export interface Notification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  /** Optional link to navigate to on click */
  href?: string;
}

interface NotificationState {
  notifications: Notification[];
  isOpen: boolean;

  // Derived
  unreadCount: () => number;

  // Actions
  open: () => void;
  close: () => void;
  toggle: () => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
  addNotification: (n: Omit<Notification, 'id' | 'read' | 'createdAt'> & { createdAt?: string }) => void;
  setNotifications: (ns: Notification[]) => void;
}

let counter = 0;

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  isOpen: false,

  unreadCount: () => get().notifications.filter((n) => !n.read).length,

  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  markAsRead: (id) => {
    // Optimistic update
    set((s) => ({
      notifications: s.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      ),
    }));
    // Persist to API (fire and forget)
    api.post('/notifications/inbox/mark-read', { notificationId: id }).catch(() => {});
  },

  markAllAsRead: () => {
    // Optimistic update
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
    }));
    // Persist to API
    api.post('/notifications/inbox/mark-all-read').catch(() => {});
  },

  dismiss: (id) => {
    // Optimistic update
    set((s) => ({
      notifications: s.notifications.filter((n) => n.id !== id),
    }));
    // Persist to API
    api.delete(`/notifications/inbox/${id}`).catch(() => {});
  },

  clearAll: () => set({ notifications: [] }),

  addNotification: (n) =>
    set((s) => ({
      notifications: [
        {
          ...n,
          id: `notif-${Date.now()}-${++counter}`,
          read: false,
          createdAt: n.createdAt ?? new Date().toISOString(),
        },
        ...s.notifications,
      ],
    })),

  setNotifications: (ns) => set({ notifications: ns }),
}));
